import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/Layout';
import { VideoGrid } from '@/components/VideoGrid';
import { FeedFilterDropdown } from '@/components/FeedFilterDropdown';
import { useVideoStream } from '@/hooks/useStreamingVideos';
import { useReportsStream } from '@/hooks/useReports';
import { useVideoFeedFilter } from '@/hooks/useVideoFeedFilter';
import { isVideoReported } from '@/lib/reportUtils';
import { isYouTubeUrl } from '@/lib/videoTypes';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') ?? '').trim();

  // NIP-50 full-text search on relays that support it (e.g.
  // relay.divine.video). Relays without NIP-50 support simply return
  // nothing — no failure.
  const stream = useVideoStream({ search: query || undefined, enabled: !!query });

  const { filter } = useVideoFeedFilter();
  const reports = useReportsStream(!!query);

  const filtered = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    return stream.videos.filter((video) => {
      if (!filter.showYouTube && isYouTubeUrl(video.videoUrl)) return false;
      if (!filter.showUnknownDuration && video.duration === undefined) return false;
      if (!filter.showReported && isVideoReported(video, reports)) return false;
      // Always verify client-side: relays without NIP-50 support ignore the
      // `search` field and return unfiltered results. Match title and
      // description; every query word must appear in one of them.
      if (words.length > 0) {
        const haystack = `${video.title}\n${video.description}`.toLowerCase();
        if (!words.every(w => haystack.includes(w))) return false;
      }
      return true;
    });
  }, [stream.videos, filter, reports, query]);

  const horizontal = filtered.filter(v => !v.isVertical);
  const vertical = filtered.filter(v => v.isVertical);

  return (
    <Layout>
      <div className="mx-auto max-w-[2000px] px-3 sm:px-6 py-5">
        <Tabs defaultValue="videos">
          {/* Two rows on mobile so a long query can't overlap the filter
              button; single row from sm: up. */}
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="flex items-center gap-2 text-lg font-semibold min-w-0">
              <Search className="h-5 w-5 shrink-0" />
              <span className="truncate">
                {query ? `"${query}"` : 'Search'}
              </span>
            </h1>
            <div className="flex items-center justify-between gap-3 shrink-0">
              <TabsList>
                <TabsTrigger value="videos">Videos</TabsTrigger>
                <TabsTrigger value="shorts">Shorts</TabsTrigger>
              </TabsList>
              <FeedFilterDropdown />
            </div>
          </div>
          <TabsContent value="videos">
            <VideoGrid
              videos={horizontal}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              emptyMessage={
                query
                  ? 'No videos found. Search matches title and description; full-text results depend on relays with NIP-50 support (e.g. relay.divine.video).'
                  : 'Enter a search query above.'
              }
            />
          </TabsContent>
          <TabsContent value="shorts">
            <VideoGrid
              videos={vertical}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              variant="shorts"
              emptyMessage="No shorts found for this search."
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
